import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { adminApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatDate, formatFileSize, getRiskColor, getStatusColor } from '../../lib/utils';
import {
  FileText,
  Search,
  Loader2,
  ExternalLink,
  RefreshCw
} from 'lucide-react';

const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const params = { limit: 100 };
        if (statusFilter !== 'all') params.status = statusFilter;
        if (riskFilter !== 'all') params.risk_level = riskFilter;
        
        const response = await adminApi.listSubmissions(params);
        setSubmissions(response.data);
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [statusFilter, riskFilter]);

  const filteredSubmissions = submissions.filter(submission =>
    submission.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    submission.file_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-submissions-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">All Submissions</h1>
          <p className="text-muted-foreground mt-1">
            Review and monitor all document submissions
          </p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title or filename..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10"
                  data-testid="submission-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 h-10" data-testid="admin-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
              <Select value={riskFilter} onValueChange={setRiskFilter}>
                <SelectTrigger className="w-full sm:w-40 h-10" data-testid="admin-risk-filter">
                  <SelectValue placeholder="Risk Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Risk Levels</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Submissions Table */}
        <Card>
          <CardHeader className="border-b border-border/40">
            <CardTitle className="font-serif">
              {filteredSubmissions.length} Submission{filteredSubmissions.length !== 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No submissions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>User ID</th>
                      <th>Status</th>
                      <th>Similarity</th>
                      <th>AI Score</th>
                      <th>Risk</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.map((submission) => (
                      <tr key={submission.id} data-testid={`admin-row-${submission.id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{submission.title}</p>
                              <p className="text-sm text-muted-foreground">
                                {submission.file_name} • {formatFileSize(submission.file_size)}
                              </p>
                              {submission.analysis_details?.resubmission_of && (
                                <Badge
                                  variant="secondary"
                                  className="mt-1 bg-blue-50 text-blue-700 border-blue-200 text-xs"
                                  title="This file was previously submitted — score carried over from the earlier analysis"
                                  data-testid="admin-resubmission-badge"
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Resubmission
                                </Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="font-mono text-sm text-muted-foreground">
                            {submission.user_id.slice(0, 8)}...
                          </span>
                        </td>
                        <td>
                          <Badge className={getStatusColor(submission.status)} variant="secondary">
                            {submission.status === 'processing' && (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            )}
                            {submission.status}
                          </Badge>
                        </td>
                        <td>
                          {submission.similarity_score !== null ? (
                            <span className="font-mono font-semibold">
                              {submission.similarity_score?.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td>
                          {submission.ai_score !== null ? (
                            <span className="font-mono">
                              {submission.ai_score?.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td>
                          {submission.risk_level ? (
                            <Badge className={getRiskColor(submission.risk_level)} variant="secondary">
                              {submission.risk_level}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(submission.created_at)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminSubmissions;
